import { ChangeLog, ImageFile, OrderDirection, PaginationInfo } from '../shared';
import { ChannelPreview } from './channelsNew';
import { graphqlApiWithPersisted } from './graphqlApi';
import { eventQuery } from './helpers/queries';
import { OrganisationTag } from './organisation';
import { UserPreview } from './user';

interface EventPermissions {
  allowToDelete: boolean;
  allowToEdit: boolean;
  allowToSeeParticipants: boolean;
  allowToEditChannels: boolean;
  allowToSeeWaitlisted: boolean;
  allowToSeeChangeLog: boolean;
}

export enum EventLocationType {
  IN_PERSON = 'IN_PERSON',
  VIRTUAL = 'VIRTUAL',
  NONE = 'NONE',
}

export interface EventLocation {
  type: EventLocationType;
  address?: string;
  virtualUrl?: string;
}

export enum EventStatus {
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHED = 'UNPUBLISHED',
  CANCELLED = 'CANCELLED',
}

export enum EventActivityStatus {
  UPCOMING = 'UPCOMING',
  SCHEDULED = 'SCHEDULED',
  PAST = 'PAST',
}

export enum EventOrderBy {
  CREATED_AT = 'CREATED_AT',
  START_AT = 'START_AT',
  END_AT = 'END_AT',
  PUBLISHED_AT = 'PUBLISHED_AT',
  SCHEDULED_AT = 'SCHEDULED_AT',
  REGISTRATION_END_AT = 'REGISTRATION_END_AT',
  UPDATED_AT = 'UPDATED_AT',
  UPCOMING = 'UPCOMING',
  UPCOMING_REGISTRATION_END_AT = 'UPCOMING_REGISTRATION_END_AT',
  UPCOMING_END_AT = 'UPCOMING_END_AT',
}

export enum EventUserStatus {
  CONFIRMED = 'CONFIRMED',
  HOST = 'HOST',
  WAITLISTED = 'WAITLISTED',
}

export enum EventType {
  DEFAULT = 'DEFAULT',
  EXTERNAL_URL = 'EXTERNAL_URL',
}

export interface Event {
  id: number;
  type: EventType;
  name: string;
  image: ImageFile | null;
  imageId: number | null;
  description: string;
  descriptionHTML: string;
  createdAt: string;
  startAt: string;
  endAt: string | null;
  scheduledAt: string | null;
  status: EventStatus;
  organisationTags: OrganisationTag[];
  participantStatus: EventUserStatus | null;
  participantsCapacity: number;
  participantsRegistrationDeadlineEndAt: string | null;
  participantCountConfirmed: number;
  participantCountHost: number;
  participantCountWaitlisted: number;
  participantsPreview: UserPreview[];
  location: EventLocation;
  permissions: EventPermissions;
  showParticipants: boolean;
  allowWaitlist: boolean;
  channels: ChannelPreview[];
  reminderHoursBeforeStart: number | null;
  externalUrl: string | null;
  externalLabel: string | null;
}

export interface EventsFilters {
  from?: string;
  to?: string;
  hostIds?: number[];
  organisationTagIds?: number[];
  query?: string;
  statuses?: EventStatus[];
  activityStatuses?: EventActivityStatus[];
  participantStatuses?: EventUserStatus[];
}

export interface EventsArgs {
  channelId: number;
  filters?: EventsFilters;
  orderBy?: EventOrderBy;
  orderDirection?: OrderDirection;
  page?: number;
  size?: number;
}

interface EventsPayload {
  items: Event[];
  pageInfo: PaginationInfo;
}

interface EventsResponse {
  data: { events: EventsPayload };
}

export interface EventByIdArgs {
  channelId: number;
  eventId: number;
}

interface EventByIdResponse {
  data: { eventById: { payload: Event } };
}

interface EventParticipantsFilters {
  query?: string;
  participantStatuses?: EventUserStatus[];
}

export interface EventParticipantsArgs {
  eventId: number;
  orderDirection?: OrderDirection;
  filters?: EventParticipantsFilters;
  page?: number;
  size?: number;
}

interface EventParticipantsPayload {
  items: UserPreview[];
  pageInfo: PaginationInfo;
}

interface EventParticipantsResponse {
  data: { eventParticipants: EventParticipantsPayload };
}

export interface EventCalendarPreview {
  id: number;
  startAt: string;
  endAt: string | null;
  participantStatus: EventUserStatus;
  status: EventStatus;
}

interface EventsCalendarFilters {
  from: string;
  to: string;
}

interface EventsCalendarArgs {
  channelId: number;
  filters: EventsCalendarFilters;
}

interface EventsCalendarResponse {
  data: { eventsCalendar: EventCalendarPreview[] };
}

interface EventCancelParticipationArgs {
  channelId: number;
  eventId: number;
}

interface EventCancelParticipationResult {
  payload: boolean;
  error: { kind: string; message: string };
}

interface EventCancelParticipationResponse {
  data: { eventCancelParticipation: EventCancelParticipationResult };
}

interface EventJoinAsParticipantArgs {
  channelId: number;
  eventId: number;
}

interface EventJoinAsParticipantResult {
  payload: EventUserStatus | null;
  error: { kind: string; message: string };
}

interface EventJoinAsParticipantResponse {
  data: { eventJoinAsParticipant: EventJoinAsParticipantResult };
}

export interface EventDeleteArgs {
  channelId: number;
  eventId: number;
}

interface EventDeleteResult {
  payload: boolean;
  error: { kind: string; message: string };
}

interface EventDeleteResponse {
  data: { eventDelete: EventDeleteResult };
}

interface EventCreateFieldsLocationInput {
  address?: string;
  virtualUrl?: string;
}

interface EventCreateFields {
  type: EventType;
  name: string;
  startAt: string;
  status: EventStatus;
  allowWaitlist?: boolean;
  endAt?: string | null;
  channelIds?: number[];
  description?: string;
  hostIds?: number[];
  imageId?: number | null;
  location?: EventCreateFieldsLocationInput;
  organisationTagIds?: number[];
  participantsCapacity?: number;
  participantsRegistrationDeadlineEndAt?: string | null;
  reminderHoursBeforeStart?: number | null;
  scheduledAt?: string | null;
  showParticipants?: boolean;
  externalUrl?: string | null;
  externalLabel?: string | null;
}

export interface EventCreateArgs {
  channelId: number;
  fields: EventCreateFields;
}

interface EventCreateResult {
  payload: Event | null;
  error: { kind: string; message: string } | null;
}

interface EventCreateResponse {
  data: { eventCreate: EventCreateResult };
}

export interface EventCreateAsCopyArgs {
  channelId: number;
  sourceChannelId: number;
  sourceEventId: number;
}

interface EventCreateAsCopyResponse {
  data: { eventCreateAsCopy: EventCreateResult };
}

interface EventUpdateFields {
  allowWaitlist?: boolean;
  channelIds?: number[];
  description?: string;
  endAt?: string | null;
  externalLabel?: string | null;
  externalUrl?: string | null;
  hostIds?: number[];
  imageId?: number | null;
  location?: EventCreateFieldsLocationInput;
  name?: string;
  organisationTagIds?: number[];
  participantsCapacity?: number;
  participantsRegistrationDeadlineEndAt?: string | null;
  reminderHoursBeforeStart?: number | null;
  scheduledAt?: string | null;
  showParticipants?: boolean;
  startAt?: string;
  status?: EventStatus;
}

export interface EventUpdateArgs {
  channelId: number;
  eventId: number;
  fields: EventUpdateFields;
}

interface EventUpdateResult {
  payload: Event;
  error: { kind: string; message: string };
}

interface EventUpdateResponse {
  data: { eventUpdate: EventUpdateResult };
}

interface EventParticipantsGenerateReportArgs {
  channelId: number;
  eventId: number;
}

interface EventParticipantsGenerateReportResult {
  payload: boolean;
  error: { kind: string; message: string };
}

interface EventParticipantsGenerateReportResponse {
  data: { eventParticipantsGenerateReport: EventParticipantsGenerateReportResult };
}

interface EventChangeLogByIdArgs {
  channelId: number;
  eventId: number;
}

interface EventChangeLogByIdResponse {
  data: { eventChangeLogById: ChangeLog[] };
}

export const eventsApi = graphqlApiWithPersisted.injectEndpoints({
  endpoints: (builder) => ({
    events: builder.query<EventsPayload, EventsArgs>({
      query: ({
        channelId,
        filters,
        orderBy = EventOrderBy.START_AT,
        orderDirection = OrderDirection.DESC,
        page = 1,
        size = 10,
      }) => ({
        url: '/graphql/webapp?events',
        method: 'POST',
        body: {
          query: `query Events($channelId: Int!, $filters: GetEventsFiltersFeedArgs, $orderBy: EventOrderByEnum!, $orderDirection: OrderDirectionEnum!, $page: Int, $size: Int) {
            events(channelId: $channelId, filters: $filters, orderBy: $orderBy, orderDirection: $orderDirection, page: $page, size: $size) {
              items {
                ${eventQuery}
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: {
            channelId,
            filters,
            orderBy,
            orderDirection,
            page,
            size,
          },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: EventsResponse) => response.data.events,
    }),
    eventById: builder.query<Event, EventByIdArgs>({
      query: ({ channelId, eventId }) => ({
        url: '/graphql/webapp?eventById',
        method: 'POST',
        body: {
          query: `query EventById($channelId: Int!, $eventId: Int!) {
            eventById(channelId: $channelId, eventId: $eventId) {
              payload {
                ${eventQuery}
              }
            }
          }`,
          variables: { channelId, eventId },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: EventByIdResponse) => response.data.eventById.payload,
    }),
    eventParticipants: builder.query<EventParticipantsPayload, EventParticipantsArgs>({
      query: ({ eventId, orderDirection = OrderDirection.DESC, filters, page, size }) => ({
        url: '/graphql/webapp?eventParticipants',
        method: 'POST',
        body: {
          query: `query EventParticipants($eventId: Int!, $orderDirection: OrderDirectionEnum!, $filters: GetEventParticipantsFiltersArgs, $page: Int, $size: Int) {
            eventParticipants(eventId: $eventId, orderDirection: $orderDirection, filters: $filters, page: $page, size: $size) {
              items {
                id
                screenName
                avatar {
                  url
                }
              }
              pageInfo {
                page
                hasNextPage
              }
            }
          }`,
          variables: { eventId, orderDirection, filters, page, size },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: EventParticipantsResponse) => response.data.eventParticipants,
    }),
    eventsCalendar: builder.query<EventCalendarPreview[], EventsCalendarArgs>({
      query: ({ channelId, filters }) => ({
        url: '/graphql/webapp?eventsCalendar',
        method: 'POST',
        body: {
          query: `query EventsCalendar($channelId: Int!, $filters: GetEventsCalendarFiltersArgs!) {
            eventsCalendar(channelId: $channelId, filters: $filters) {
              id
              startAt
              endAt
              participantStatus
              status
            }
          }`,
          variables: { channelId, filters },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: EventsCalendarResponse) => response.data.eventsCalendar,
    }),
    eventJoinAsParticipant: builder.mutation<EventUserStatus | null, EventJoinAsParticipantArgs>({
      query: ({ channelId, eventId }) => ({
        url: '/graphql/webapp?eventJoinAsParticipant',
        method: 'POST',
        body: {
          query: `mutation EventJoinAsParticipant($input: EventJoinAsParticipantInput!) {
            eventJoinAsParticipant(input: $input) {
              payload
              error {
                __typename
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, eventId } },
        },
      }),
      transformResponse: (response: EventJoinAsParticipantResponse) =>
        response.data.eventJoinAsParticipant.payload,
    }),
    eventCancelParticipation: builder.mutation<boolean, EventCancelParticipationArgs>({
      query: ({ channelId, eventId }) => ({
        url: '/graphql/webapp?eventCancelParticipation',
        method: 'POST',
        body: {
          query: `mutation EventCancelParticipation($input: EventCancelParticipationInput!) {
            eventCancelParticipation(input: $input) {
              payload
              error {
                __typename
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, eventId } },
        },
      }),
      transformResponse: (response: EventCancelParticipationResponse) =>
        response.data.eventCancelParticipation.payload,
    }),
    eventDelete: builder.mutation<boolean, EventDeleteArgs>({
      query: ({ channelId, eventId }) => ({
        url: '/graphql/webapp?eventDelete',
        method: 'POST',
        body: {
          query: `mutation EventDelete($input: EventDeleteInput!) {
            eventDelete(input: $input) {
              payload
              error {
                __typename
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, eventId } },
        },
      }),
      transformResponse: (response: EventDeleteResponse) => response.data.eventDelete.payload,
    }),
    eventCreate: builder.mutation<EventCreateResult, EventCreateArgs>({
      query: ({ channelId, fields }) => ({
        url: '/graphql/webapp?eventCreate',
        method: 'POST',
        body: {
          query: `mutation EventCreate($input: EventCreateInput!) {
            eventCreate(input: $input) {
              payload {
                ${eventQuery}
              }
              error {
                __typename
                ... on EventValidationError {
                  message
                }
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, fields } },
        },
      }),
      transformResponse: (response: EventCreateResponse) => response.data.eventCreate,
    }),
    eventCreateAsCopy: builder.mutation<EventCreateResult, EventCreateAsCopyArgs>({
      query: ({ channelId, sourceChannelId, sourceEventId }) => ({
        url: '/graphql/webapp?eventCreateAsCopy',
        method: 'POST',
        body: {
          query: `mutation EventCreateAsCopy($input: EventCreateAsCopyInput!) {
            eventCreateAsCopy(input: $input) {
              payload {
                ${eventQuery}
              }
              error {
                __typename
                ... on EventValidationError {
                  message
                }
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, sourceChannelId, sourceEventId } },
        },
      }),
      transformResponse: (response: EventCreateAsCopyResponse) => response.data.eventCreateAsCopy,
    }),
    eventUpdate: builder.mutation<Event, EventUpdateArgs>({
      query: ({ channelId, eventId, fields }) => ({
        url: '/graphql/webapp?eventUpdate',
        method: 'POST',
        body: {
          query: `mutation EventUpdate($input: EventUpdateInput!) {
            eventUpdate(input: $input) {
              payload {
                ${eventQuery}
              }
              error {
                __typename
                ... on EventValidationError {
                  message
                }
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
          variables: { input: { channelId, eventId, fields } },
        },
      }),
      transformResponse: (response: EventUpdateResponse) => response.data.eventUpdate.payload,
    }),
    eventParticipantsGenerateReport: builder.mutation<boolean, EventParticipantsGenerateReportArgs>(
      {
        query: ({ channelId, eventId }) => ({
          url: '/graphql/webapp?eventParticipantsGenerateReport',
          method: 'POST',
          body: {
            query: `mutation EventParticipantsGenerateReport($input: EventParticipantsGenerateReport!) {
            eventParticipantsGenerateReport(input: $input) {
              payload
              error {
                __typename
                ... on EventReadOnlyAccessError {
                  message
                }
                ... on UnknownError {
                  kind
                  message
                }
              }
            }
          }`,
            variables: { input: { channelId, eventId } },
          },
        }),
        transformResponse: (response: EventParticipantsGenerateReportResponse) =>
          response.data.eventParticipantsGenerateReport.payload,
      }
    ),
    eventChangeLogById: builder.query<ChangeLog[], EventChangeLogByIdArgs>({
      query: ({ channelId, eventId }) => ({
        url: '/graphql/webapp?eventChangeLogById',
        method: 'POST',
        body: {
          query: `query EventChangeLogById($channelId: Int!, $eventId: Int!) {
            eventChangeLogById(channelId: $channelId, eventId: $eventId) {
              id
              createdAt
              changes
              user { id screenName email }
            }
          }`,
          variables: { channelId, eventId },
        },
      }),
      forceRefetch: () => true,
      transformResponse: (response: EventChangeLogByIdResponse) => response.data.eventChangeLogById,
    }),
  }),
});

export const {
  useEventByIdQuery,
  useEventChangeLogByIdQuery,
  useLazyEventByIdQuery,
  useEventsQuery,
  useLazyEventsQuery,
  useEventsCalendarQuery,
  useLazyEventParticipantsQuery,
  useEventCreateAsCopyMutation,
  useEventCreateMutation,
  useEventParticipantsGenerateReportMutation,
  useEventUpdateMutation,
} = eventsApi;
